import type { Reference } from '@apollo/client';
import { useMutation } from '@apollo/client';
import type { Op } from 'quill/core';
import Quill from 'quill/core';
import React, { useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import { useQuill, withQuillContextProvider } from '../../editor/QuillContext';
import { CREATION_ID } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import addLinkIcon from '../../icons/non-prefilled/16x16/addLink.svg';
import unlinkIcon from '../../icons/non-prefilled/16x16/unlink.svg';
import {
  MagneticWallItemActionContainer,
  MagneticWallItemEmojiPickerButton,
  MagneticWallItemIconButton,
  MagneticWallItemSubmitButton,
  MagneticWallItemWithAvatar,
} from '../MagneticWallItem';
import showFullPageSheet from '../comment/showFullPageSheet';
import getCurrentMarkForComment from '../comment/utils/getCurrentMarkForComment';
import styles from './AnnotationCreate.module.scss';
import AnnotationIcon from './AnnotationIcon';
import AnnotationInput from './AnnotationInput';
import AnnotationLinkInput from './AnnotationLinkInput';
import type { AnnotationLinkState } from './useAnnotationLink';
import useUpdateAnnotation from './useUpdateAnnotation';

const ANNOTATION_CREATE_ANNOTATION_FRAGMENT = gql(`
  fragment AnnotationCreate_annotation on PublicAnnotationFields {
    id
    content
    link
  }
`);

type AnnotationFragment = DocumentType<
  typeof ANNOTATION_CREATE_ANNOTATION_FRAGMENT
>;

const CREATE_ANNOTATION = gql(`
  mutation createAnnotation(
    $checksum: Int!
    $content: Json!
    $link: String
    $mark: Json!
    $postId: ID!
    $version: Int!
  ) {
    createAnnotation(
      checksum: $checksum
      content: $content
      mark: $mark
      link: $link
      postId: $postId
      version: $version
    ) {
      ...AnnotationCreate_annotation
    }
  }
`);

const isContentEmpty = (content: Op[]) => {
  return (
    content.length === 0 ||
    (content.length === 1 && content[0]?.insert === '\n')
  );
};

const insertText = (quill: Quill, text: string) => {
  const { index, length } = quill.getSelection(true);
  quill.insertText(index + length, text, Quill.sources.USER);
  quill.setSelection(index + length + text.length, 0, Quill.sources.USER);
};

interface AnnotationCreateProps {
  annotation: AnnotationFragment;
  initialContent: Op[];
  postId: string;
  onChange(ops: Op[]): void;
  onSaved(annotation: AnnotationFragment): void;
  annotationLinkState: AnnotationLinkState;
  isLinkInputOpen: boolean;
  setIsLinkInputOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AnnotationCreate = ({
  annotation,
  onSaved,
  onChange,
  initialContent,
  postId,
  annotationLinkState,
  isLinkInputOpen,
  setIsLinkInputOpen,
}: AnnotationCreateProps) => {
  const isNewAnnotation = annotation.id === CREATION_ID;
  const { quill, ancestorQuill } = useQuill();
  const [content, setContent] = useState<Op[]>(initialContent);
  const sync = useAppSelector((state) => state.editor.sync);
  const readOnly = useAppSelector((state) => state.editor.readOnly);

  const [createAnnotation, { loading: isCreatingAnnotation }] = useMutation(
    CREATE_ANNOTATION,
    {
      update: (proxy, { data }) => {
        if (data?.createAnnotation) {
          proxy.modify({
            id: proxy.identify({ __typename: 'Post', id: postId }),
            fields: {
              annotations: (
                existingAnnotations: readonly Reference[],
                { readField },
              ) => {
                const newAnnotationRef = proxy.writeFragment({
                  data: data.createAnnotation,
                  fragment: ANNOTATION_CREATE_ANNOTATION_FRAGMENT,
                });

                if (
                  newAnnotationRef == null ||
                  existingAnnotations.some(
                    (ref) => readField('id', ref) === data.createAnnotation.id,
                  )
                ) {
                  return [...existingAnnotations];
                }

                return [newAnnotationRef, ...existingAnnotations];
              },
            },
          });
        }
      },
    },
  );

  const [updateAnnotation, { loading: isUpdatingAnnotation }] =
    useUpdateAnnotation();

  const isSaving = isCreatingAnnotation || isUpdatingAnnotation;

  const handleSave = async () => {
    if (isNewAnnotation) {
      if (!sync) throw new Error('sync not inited');
      if (!ancestorQuill) throw new Error('quill not inited');

      const { data } = await createAnnotation({
        variables: {
          content: JSON.stringify(content),
          mark: JSON.stringify(getCurrentMarkForComment(ancestorQuill)),
          postId,
          link: annotationLinkState.link,
          version: sync.version,
          checksum: sync.checksum,
        },
      });

      if (data?.createAnnotation) {
        onSaved(data.createAnnotation);
      }
    } else {
      const { data } = await updateAnnotation({
        variables: {
          id: annotation.id,
          content: JSON.stringify(content),
          link: annotationLinkState.link,
        },
      });

      if (data?.updateAnnotation) {
        onSaved(data.updateAnnotation);
      }
    }
  };

  const handleChange = (newContent: Op[]) => {
    setContent(newContent);
    onChange(newContent);
  };

  const hasAutoFocus =
    (!showFullPageSheet() || isNewAnnotation) &&
    (readOnly || !quill?.hasFocus());

  return (
    <div className={styles.container}>
      <MagneticWallItemWithAvatar avatar={<AnnotationIcon hasContainer />}>
        <AnnotationInput
          onChange={handleChange}
          autoFocus={hasAutoFocus}
          initialContent={initialContent}
          quill={quill}
          onSave={handleSave}
        />
      </MagneticWallItemWithAvatar>
      {isLinkInputOpen && (
        <div>
          <AnnotationLinkInput
            value={annotationLinkState.value}
            onChange={annotationLinkState.onChange}
            onSave={annotationLinkState.onSave}
          />
        </div>
      )}
      <MagneticWallItemActionContainer>
        <MagneticWallItemEmojiPickerButton
          onPick={(emoji) => {
            if (quill) {
              insertText(quill, emoji);
            }
          }}
        />
        {annotation.link ? (
          <MagneticWallItemIconButton
            icon={unlinkIcon}
            onClick={() => annotationLinkState.onRemove()}
            tooltip="Remove reference"
            testId="remove-reference-link-button"
          />
        ) : (
          <MagneticWallItemIconButton
            icon={addLinkIcon}
            onClick={() => setIsLinkInputOpen((isOpen) => !isOpen)}
            tooltip="Add reference"
            testId="reference-link-button"
            active={isLinkInputOpen}
          />
        )}

        <MagneticWallItemSubmitButton
          disabled={isSaving || isContentEmpty(content)}
          onClick={handleSave}
        >
          {isNewAnnotation ? 'Annotate' : 'Save'}
        </MagneticWallItemSubmitButton>
      </MagneticWallItemActionContainer>
    </div>
  );
};

export default withQuillContextProvider(AnnotationCreate);
